import React, { useState } from 'react';
import {
	AlertBox,
	ExtendedTheme,
	Heading,
	Button,
	Dialog,
	DialogContent,
	TextBlock,
	UserInterfacePrint,
	UserInterfaceSocialShare,
	UserInterfaceMail, UserInterfaceTwitter, UserInterfaceFacebook, UserInterfaceLinkedin
} from 'neon-design-system';
import { createStyles, makeStyles } from '@material-ui/core';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton, EmailShareButton } from 'react-share';
import { CampaignType } from '../../shared/types';
import { useSocialShare } from '../hooks/use-social-share';

interface ShareArticleDialogProps {
	handleClose: () => void;
	articleId: string;
	articleTitle: string;
}

const useStyles = makeStyles<ExtendedTheme>(theme => {
	return createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			'@media print': {
				display: 'none'
			}
		},
		actions: {
			display: 'flex',
			marginBottom: theme.spacing(4)
		},
		shareButton: {
			marginRight: theme.spacing(2)
		},
		socialMediaButton: {
			padding: 0,
			height: 32,
			width: 32,
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			outline: 'none',
			'& svg': {
				fill: theme.themeColors.grey[200]
			},
			'&:hover': {
				'& svg': {
					fill: theme.palette.common.white
				}
			}
		},
		emptyButton: {
			border: 'none',
			outline: 'none',
			background: 'none',
			cursor: 'pointer'
		}
	})
});

const ShareArticleActions = (props: { articleId: string; articleTitle: string }) => {
	const classes = useStyles();
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const linkedInShare = useSocialShare(CampaignType.LinkedIn, props.articleId);
	const twitterShare = useSocialShare(CampaignType.Twitter, props.articleId);
	const facebookShare = useSocialShare(CampaignType.Facebook, props.articleId);
	const emailShare = useSocialShare(CampaignType.Email, props.articleId);

	const print = () => {
		window.print();
	}

	const openDialog = () => {
		setIsDialogOpen(true);
	}

	const closeDialog = () => {
		setIsDialogOpen(false);
	}

	return (
		<>
			<div className={classes.root}>
				<div className={classes.actions}>
					<button
						id="print-article"
						onClick={print}
						className={[classes.socialMediaButton, classes.emptyButton].join(' ')}
					>
						<UserInterfacePrint />
					</button>
					<button
						id="share-article-button"
						onClick={openDialog}
						className={[classes.socialMediaButton, classes.emptyButton].join(' ')}
					>
						<UserInterfaceSocialShare />
					</button>
					<EmailShareButton
						url={emailShare.url}
						subject={props.articleTitle}
						id="share-article-via-email-button"
						className={classes.socialMediaButton}
						translate=""
					>
						<span className={classes.emptyButton} {...emailShare}>
							<UserInterfaceMail />
						</span>
					</EmailShareButton>
					<TwitterShareButton
						url={twitterShare.url}
						id="share-article-button-twitter"
						className={classes.socialMediaButton}
						translate=""
					>
						<span className={classes.emptyButton} {...twitterShare}>
							<UserInterfaceTwitter />
						</span>
					</TwitterShareButton>
					<FacebookShareButton
						url={facebookShare.url}
						id="share-article-button-facebook"
						className={classes.socialMediaButton}
						translate=""
					>
						<span className={classes.emptyButton} {...facebookShare}>
							<UserInterfaceFacebook />
						</span>
					</FacebookShareButton>
					<LinkedinShareButton
						url={linkedInShare.url}
						id="share-article-button-linked-in"
						className={classes.socialMediaButton}
						translate=""
					>
						<span className={classes.emptyButton} {...linkedInShare}>
							<UserInterfaceLinkedin />
						</span>
					</LinkedinShareButton>
				</div>
			</div>
			{
				isDialogOpen &&
				<ShareArticleDialog
					handleClose={closeDialog}
					articleId={props.articleId}
					articleTitle={props.articleTitle}
				/>
			}
		</>
	);
};

const useShareArticleDialogStyles = makeStyles<ExtendedTheme>(theme => {
	const socialBtnWrapper = {
		height: 72,
		minWidth: 48,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 0,
		outline: 'none',
		flexDirection: 'column'
	};
	const socialBtn = {
		height: 48,
		minHeight: 48,
		maxHeight: 48,
		width: 48,
		minWidth: 48,
		maxWidth: 48,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 100,
		marginBottom: theme.spacing(1)
	}
	const emptyButton = {
		border: 'none',
		outline: 'none',
		background: 'none',
		cursor: 'pointer'
	}
	return createStyles({
		root: {
			maxWidth: 520,
			[theme.breakpoints.down('xs')]: {
				marginBottom: theme.spacing(6)
			}
		},
		linkCard: {
			backgroundColor: theme.themeColors.grey[700],
			borderRadius: 3,
			padding: theme.spacing(2),
			'&:not(:first-child)': {
				margin: theme.spacing(2, 0, 0, 0),
				[theme.breakpoints.down('xs')]: {
					paddingBottom: 0
				}
			}
		},
		linkWrapper: {
			border: `1px solid ${theme.themeColors.grey[500]}`,
			borderRadius: 2,
			padding: theme.spacing(1),
			cursor: 'text',
			margin: theme.spacing(2, 0),
			color: theme.palette.common.white
		},
		linkText: {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis'
		},
		socialActionsWrapper: {
			display: 'flex',
			marginTop: theme.spacing(2),
			'& > :not(:last-child)': {
				marginRight: theme.spacing(2.625),
				[theme.breakpoints.up('sm')]: {
					marginRight: theme.spacing(4.3125)
				}
			},
			[theme.breakpoints.down('xs')]: {
				flexDirection: 'column',
				alignItems: 'flex-start',
				marginTop: theme.spacing(1)
			}
		},
		socialButton: {
			...socialBtnWrapper as any,
			[theme.breakpoints.down('xs')]: {
				flexDirection: 'row',
				'&:not(:last-child)': {
					marginBottom: theme.spacing(1)
				}
			},
			'& svg': {
				fill: theme.palette.common.white
			}
		},
		twitterButton: {
			...socialBtn,
			...emptyButton,
			backgroundColor: '#00ACEE !important',
			'&:hover': {
				backgroundColor: '#178ebb !important'
			},
			[theme.breakpoints.down('xs')]: {
				marginRight: theme.spacing(1)
			}
		},
		facebookButton: {
			...socialBtn,
			...emptyButton,
			backgroundColor: '#3B5998 !important',
			'&:hover': {
				backgroundColor: '#284173 !important'
			},
			[theme.breakpoints.down('xs')]: {
				marginRight: theme.spacing(1)
			}
		},
		linkedInButton: {
			...socialBtn,
			...emptyButton,
			backgroundColor: '#0E76A8 !important',
			'&:hover': {
				backgroundColor: '#195977 !important'
			},
			[theme.breakpoints.down('xs')]: {
				marginRight: theme.spacing(1)
			}
		},
		emailButton: {
			...socialBtn,
			...emptyButton,
			backgroundColor: theme.themeColors.warning.main,
			'&:hover': {
				backgroundColor: theme.themeColors.warning.dark
			},
			[theme.breakpoints.down('xs')]: {
				marginRight: theme.spacing(1)
			}
		},
		hiddenInput: {
			position: 'fixed',
			top: -10000
		},
		input: {
			color: theme.themeColors.grey[200],
			marginTop: theme.spacing(2)
		},
		sendButton: {
			marginTop: theme.spacing(2)
		},
		dialog: {
			'& > div > div': {
				width: 520
			}
		}
	})
});

const ShareArticleDialog = (props: ShareArticleDialogProps) => {
	const classes = useShareArticleDialogStyles();
	const { handleClose } = props;
	const [isLinkAlertShown, setIsLinkAlertShown] = useState<boolean>(false);
	let inputRef: HTMLInputElement;
	const linkedInShare = useSocialShare(CampaignType.LinkedIn, props.articleId);
	const twitterShare = useSocialShare(CampaignType.Twitter, props.articleId);
	const facebookShare = useSocialShare(CampaignType.Facebook, props.articleId);
	const directLinkShare = useSocialShare(CampaignType.Link, props.articleId, window.location.href);
	const emailShare = useSocialShare(CampaignType.Email, props.articleId);

	const showLinkAlert = () => {
		setIsLinkAlertShown(true);
	}

	const hideLinkAlert = () => {
		setIsLinkAlertShown(false);
	}

	const copyToClipboard = () => {
		inputRef.select();
		inputRef.setSelectionRange(0, 99999);
		document.execCommand('copy');
		showLinkAlert();
	}

	return (
		<Dialog
			id="share-article-dialog"
			className={classes.dialog}
			open={true}
			handleClose={handleClose}
			title="Share article">
			<DialogContent>
				<div className={classes.root}>
					<AlertBox
						id="link-copied-to-clipboard-alert"
						onClose={hideLinkAlert}
						visible={isLinkAlertShown}
						styleName="success">Link copied to clipboard</AlertBox>
					<div className={classes.linkCard}>
						<Heading
							xs="medium"
							styleName="headline3">Copy a link to share</Heading>
						<div className={classes.linkWrapper}>
							<TextBlock
								styleName="body"
								xs="small"
								className={`${classes.linkText} share-link`}>{directLinkShare.url}</TextBlock>
							<input
								value={directLinkShare.url}
								readOnly
								ref={(ref: HTMLInputElement) => {
									inputRef = ref;
								}}
								type="text"
								className={classes.hiddenInput} />
						</div>
						<div onClick={copyToClipboard} ref={directLinkShare.ref}>
							<Button id="copy-link-button" onClick={directLinkShare.onClick} colorType="primary">
								Copy link
							</Button>
						</div>
					</div>
					<div className={classes.linkCard}>
						<Heading
							xs="medium"
							styleName="headline3">Share it on Social Media</Heading>
						<div className={classes.socialActionsWrapper}>
							<EmailShareButton
								url={emailShare.url}
								subject={props.articleTitle}
								id="share-button-email"
								className={classes.socialButton}
								translate="">
								<span className={classes.emailButton} {...emailShare}>
									<UserInterfaceMail />
								</span>
								<TextBlock styleName="caption1">Email</TextBlock>
							</EmailShareButton>
							<TwitterShareButton
								url={twitterShare.url}
								id="share-button-twitter"
								className={classes.socialButton}
								translate="">
								<span className={classes.twitterButton} {...twitterShare}>
									<UserInterfaceTwitter />
								</span>
								<TextBlock styleName="caption1">Twitter</TextBlock>
							</TwitterShareButton>
							<FacebookShareButton
								url={facebookShare.url}
								id="share-button-facebook"
								className={classes.socialButton}
								translate="">
								<span className={classes.facebookButton} {...facebookShare}>
									<UserInterfaceFacebook />
								</span>
								<TextBlock styleName="caption1">Facebook</TextBlock>
							</FacebookShareButton>
							<LinkedinShareButton
								url={linkedInShare.url}
								id="share-button-linked-in"
								className={classes.socialButton}
								translate="">
								<span className={classes.linkedInButton} {...linkedInShare}>
									<UserInterfaceLinkedin />
								</span>
								<TextBlock styleName="caption1">LinkedIn</TextBlock>
							</LinkedinShareButton>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ShareArticleActions;
